/**
 * **Die gesetzlichen Feiertage in Deutschland, je Bundesland** (08.10.2026).
 *
 * Luca: *„Feiertage zählen weiter als Miettag. … Ich würde das erst mal so
 * lassen, dass es immer ein Miettag ist, aber bereite das vor, dass bei der
 * Angebotsstellung oder bei Schnellvermietungen, wenn es einen Feiertag
 * dazwischen gibt, das angezeigt wird."*
 *
 * **Diese Datei rechnet keinen Betrag.** Sie sagt nur, welcher Tag ein
 * Feiertag ist; `zaehltAlsMiettag` (`kalender.ts`) liest sie nicht, und das
 * ist die Entscheidung vom 08.10.2026: Ein Feiertag ist ein Miettag. Die
 * Oberfläche zeigt einen Hinweis, sonst nichts (Rechenreferenz §3.1).
 *
 * **Vorbereitet, nicht gebaut — „Feiertage nicht berechnen".** Will der
 * Betrieb einmal Feiertage aus der Berechnung nehmen, ist der Weg: eine
 * zweite Einstellung neben `tenant_settings.holiday_region`, die
 * `zaehltAlsMiettag` die Liste aus `feiertageImZeitraum` mitgibt — und dann
 * die ganze Planung (Staffel, Vorabend 0286, Periodenschnitt, Vorbereitung
 * 0175) dagegen prüfen. Offen in docs/12-offene-fragen.md.
 *
 * Gerechnet, nicht abgerufen: Ostern nach Gauß in der Form von Meeus/Jones/
 * Butcher (gregorianisch), alles Bewegliche davon abgeleitet. Kein Netz, keine
 * Liste, die jemand jedes Jahr nachtragen muss.
 *
 * **Nur landesweite Feiertage.** Was nur in einzelnen Gemeinden gilt —
 * Mariä Himmelfahrt in den katholischen Gemeinden Bayerns, Fronleichnam in
 * Teilen Sachsens und Thüringens, das Augsburger Friedensfest — steht nicht
 * darin, ebenso wenig einmalige Tage (Reformationstag 2017 bundesweit,
 * 8. Mai 2020/2025 in Berlin). Es ist ein Hinweis; ein fehlender Tag kostet
 * keinen Euro.
 */
/** Die sechzehn Länder, als amtliches Kürzel (ISO 3166-2:DE ohne „DE-"). */
export const BUNDESLAENDER = [
    'BW', 'BY', 'BE', 'BB', 'HB', 'HH', 'HE', 'MV',
    'NI', 'NW', 'RP', 'SL', 'SN', 'ST', 'SH', 'TH',
];
/** Die Vorgabe, wenn nichts eingestellt ist und die Anschrift nichts verrät. */
export const BUNDESLAND_VORGABE = 'NI';
export const istBundesland = (wert) => typeof wert === 'string' && BUNDESLAENDER.includes(wert);
const zwei = (n) => String(n).padStart(2, '0');
const iso = (jahr, monat, tag) => `${jahr}-${zwei(monat)}-${zwei(tag)}`;
/** Ein ISO-Tag plus n Tage — gerechnet in UTC, damit keine Zeitzone stört. */
function plusTage(tag, n) {
    const d = new Date(`${tag}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}
/**
 * Ostersonntag im gregorianischen Kalender (Gauß, in der Form von Meeus).
 * 2026 → `2026-04-05`, 2027 → `2027-03-28`.
 */
export function ostersonntag(jahr) {
    const a = jahr % 19;
    const b = Math.floor(jahr / 100);
    const c = jahr % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const monat = Math.floor((h + l - 7 * m + 114) / 31);
    const tag = ((h + l - 7 * m + 114) % 31) + 1;
    return iso(jahr, monat, tag);
}
/** Buß- und Bettag: der Mittwoch vor dem 23. November (16.–22.11.). */
function bussUndBettag(jahr) {
    const d = new Date(Date.UTC(jahr, 10, 22));
    // Wochentag 0 = Sonntag … 3 = Mittwoch; zurück bis zum Mittwoch.
    const zurueck = (d.getUTCDay() - 3 + 7) % 7;
    d.setUTCDate(d.getUTCDate() - zurueck);
    return d.toISOString().slice(0, 10);
}
const fest = (monat, tag) => (jahr) => iso(jahr, monat, tag);
const ostern = (n) => (_jahr, os) => plusTage(os, n);
/* Die Liste in der Reihenfolge eines Jahres. Quelle: die Feiertagsgesetze der
   Länder (Stand 2026). Ostersonntag und Pfingstsonntag sind nur dort
   genannt, wo das Landesgesetz sie ausdrücklich als Feiertag führt. */
const REGELN = [
    { schluessel: 'neujahr', laender: 'alle', tag: fest(1, 1) },
    { schluessel: 'heilige_drei_koenige', laender: ['BW', 'BY', 'ST'], tag: fest(1, 6) },
    // Berlin seit 2019, Mecklenburg-Vorpommern seit 2023.
    { schluessel: 'frauentag', laender: ['BE'], ab: 2019, tag: fest(3, 8) },
    { schluessel: 'frauentag', laender: ['MV'], ab: 2023, tag: fest(3, 8) },
    { schluessel: 'karfreitag', laender: 'alle', tag: ostern(-2) },
    { schluessel: 'ostersonntag', laender: ['BB', 'HE'], tag: ostern(0) },
    { schluessel: 'ostermontag', laender: 'alle', tag: ostern(1) },
    { schluessel: 'tag_der_arbeit', laender: 'alle', tag: fest(5, 1) },
    { schluessel: 'himmelfahrt', laender: 'alle', tag: ostern(39) },
    { schluessel: 'pfingstsonntag', laender: ['BB', 'HE'], tag: ostern(49) },
    { schluessel: 'pfingstmontag', laender: 'alle', tag: ostern(50) },
    { schluessel: 'fronleichnam', laender: ['BW', 'BY', 'HE', 'NW', 'RP', 'SL'], tag: ostern(60) },
    { schluessel: 'mariae_himmelfahrt', laender: ['SL'], tag: fest(8, 15) },
    // Thüringen seit 2019.
    { schluessel: 'weltkindertag', laender: ['TH'], ab: 2019, tag: fest(9, 20) },
    { schluessel: 'deutsche_einheit', laender: 'alle', tag: fest(10, 3) },
    { schluessel: 'reformationstag', laender: ['BB', 'MV', 'SN', 'ST', 'TH'], tag: fest(10, 31) },
    // Bremen, Hamburg, Niedersachsen und Schleswig-Holstein seit 2018.
    { schluessel: 'reformationstag', laender: ['HB', 'HH', 'NI', 'SH'], ab: 2018, tag: fest(10, 31) },
    { schluessel: 'allerheiligen', laender: ['BW', 'BY', 'NW', 'RP', 'SL'], tag: fest(11, 1) },
    { schluessel: 'buss_und_bettag', laender: ['SN'], tag: (jahr) => bussUndBettag(jahr) },
    { schluessel: 'erster_weihnachtstag', laender: 'alle', tag: fest(12, 25) },
    { schluessel: 'zweiter_weihnachtstag', laender: 'alle', tag: fest(12, 26) },
];
/** Die Feiertage eines Jahres in einem Land, nach Datum geordnet. */
export function feiertageImJahr(jahr, land) {
    const os = ostersonntag(jahr);
    return REGELN
        .filter((r) => (r.laender === 'alle' || r.laender.includes(land)) && (r.ab === undefined || jahr >= r.ab))
        .map((r) => ({ tag: r.tag(jahr, os), schluessel: r.schluessel }))
        .sort((a, b) => a.tag.localeCompare(b.tag));
}
/**
 * Die Feiertage zwischen `von` und `bis`, **beide Tage eingeschlossen**
 * (`YYYY-MM-DD`; ein längerer Text wird auf den Tag gekürzt). Liegt `bis` vor
 * `von` oder fehlt ein Ende, ist die Liste leer.
 */
export function feiertageImZeitraum(von, bis, land) {
    const a = von.slice(0, 10);
    const b = bis.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b) || b < a)
        return [];
    const aus = [];
    for (let jahr = Number(a.slice(0, 4)); jahr <= Number(b.slice(0, 4)); jahr += 1) {
        for (const f of feiertageImJahr(jahr, land))
            if (f.tag >= a && f.tag <= b)
                aus.push(f);
    }
    return aus;
}
/** Ist dieser Tag (`YYYY-MM-DD`) ein Feiertag? Dann welcher. */
export function feiertagAm(tag, land) {
    return feiertageImZeitraum(tag, tag, land)[0] ?? null;
}
/* Das Land zu den ersten beiden Ziffern der Postleitzahl — die **Mehrheit**
   des Leitbereichs. Grenzbereiche sind gemischt (49477 Ibbenbüren liegt in
   NRW, 27568 Bremerhaven in Bremen); deshalb ist das nur die Vorgabe der
   Auswahl, die der Betrieb in den Einstellungen überschreibt. */
const LEITBEREICH = {
    '01': 'SN', '02': 'SN', '03': 'BB', '04': 'SN', '06': 'ST', '07': 'TH', '08': 'SN', '09': 'SN',
    '10': 'BE', '12': 'BE', '13': 'BE', '14': 'BB', '15': 'BB', '16': 'BB', '17': 'MV', '18': 'MV', '19': 'MV',
    '20': 'HH', '21': 'HH', '22': 'HH', '23': 'SH', '24': 'SH', '25': 'SH', '26': 'NI', '27': 'NI', '28': 'HB', '29': 'NI',
    '30': 'NI', '31': 'NI', '32': 'NW', '33': 'NW', '34': 'HE', '35': 'HE', '36': 'HE', '37': 'NI', '38': 'NI', '39': 'ST',
    '40': 'NW', '41': 'NW', '42': 'NW', '44': 'NW', '45': 'NW', '46': 'NW', '47': 'NW', '48': 'NW', '49': 'NI',
    '50': 'NW', '51': 'NW', '52': 'NW', '53': 'NW', '54': 'RP', '55': 'RP', '56': 'RP', '57': 'NW', '58': 'NW', '59': 'NW',
    '60': 'HE', '61': 'HE', '63': 'HE', '64': 'HE', '65': 'HE', '66': 'SL', '67': 'RP', '68': 'BW', '69': 'BW',
    '70': 'BW', '71': 'BW', '72': 'BW', '73': 'BW', '74': 'BW', '75': 'BW', '76': 'BW', '77': 'BW', '78': 'BW', '79': 'BW',
    '80': 'BY', '81': 'BY', '82': 'BY', '83': 'BY', '84': 'BY', '85': 'BY', '86': 'BY', '87': 'BY', '88': 'BW', '89': 'BW',
    '90': 'BY', '91': 'BY', '92': 'BY', '93': 'BY', '94': 'BY', '95': 'BY', '96': 'BY', '97': 'BY', '98': 'TH', '99': 'TH',
};
/** Das Land zur Postleitzahl, wenn sie eine deutsche ist — sonst `null`. */
export function bundeslandAusPlz(plz) {
    const ziffern = (plz ?? '').trim();
    if (!/^\d{5}$/.test(ziffern))
        return null;
    return LEITBEREICH[ziffern.slice(0, 2)] ?? null;
}
/**
 * Welches Land gilt: das eingestellte, sonst das aus der Firmenanschrift,
 * sonst Niedersachsen. Dieselbe Kette in Oberfläche und Einstellungen.
 */
export function feiertagsregion(eingestellt, plz) {
    if (istBundesland(eingestellt))
        return eingestellt;
    return bundeslandAusPlz(plz) ?? BUNDESLAND_VORGABE;
}
//# sourceMappingURL=feiertage.js.map